import React, { useState, useEffect } from "react"
import { Table, Button, Form, Input, Modal, Popconfirm, Divider, Select, Tag } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import productData from "../product_data/index"
import mockData from "../mockData.json"

const { Option } = Select

const problemOptions = mockData.map((item) => item.Analys)

const colorMap = {}
const colors = [
    "magenta",
    "red",
    "volcano",
    "orange",
    "gold",
    "lime",
    "green",
    "cyan",
    "blue",
    "geekblue",
    "purple",
    "pink",
    "yellow",
    "cadetblue"
]

problemOptions.forEach((problem, index) => {
    colorMap[problem] = colors[index % colors.length]
})

const transformProductData = (data) => {
    const products = []
    const specificationsSet = new Set()

    const flattenSpecifications = (specs, prefix = "") => {
        return Object.entries(specs).flatMap(([key, value]) => {
            const newKey = prefix ? `${prefix}.${key}` : key
            if (typeof value === "object" && !Array.isArray(value)) {
                return flattenSpecifications(value, newKey)
            }
            if (Array.isArray(value)) {
                return value.flatMap((v, i) =>
                    typeof v === "object" ? flattenSpecifications(v, `${newKey}.${i}`) : { key: `${newKey}.${i}`, value: v }
                )
            }
            specificationsSet.add(newKey)
            return { key: newKey, value }
        })
    }

    Object.values(data).forEach((product) => {
        product.Modeller.forEach((model) => {
            products.push({
                key: model.Artikelnummer[0]["Art.nr."] + "-" + model.Modell,
                name: product.Product,
                model: model.Modell,
                description: product.Problemlösning.map((p) => p.name),
                articleNumbers: model.Artikelnummer.map((a) => a["Art.nr."]).join(", "),
                price: model.Artikelnummer[0].Grundpris,
                specifications: model.Specifikationer ? flattenSpecifications(model.Specifikationer) : [],
                filterbeds: model.Filterbäddar || [],
                controlunits: model.Styrenheter || [],
                options: model.Tillval || []
            })
        })
    })

    return { products, specificationsSet }
}

const parseSpecificationValue = (value) => {
    if (typeof value === "string" && !isNaN(value)) {
        return parseFloat(value)
    }
    return value
}

const ProductManagement = () => {
    const [products, setProducts] = useState([])
    const [specificationsOptions, setSpecificationsOptions] = useState([])
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [editingProduct, setEditingProduct] = useState(null)
    const [sortedInfo, setSortedInfo] = useState({})
    const [searchText, setSearchText] = useState("")

    useEffect(() => {
        const { products: transformedProducts, specificationsSet } = transformProductData(productData)
        setProducts(transformedProducts)
        setSpecificationsOptions([...specificationsSet])
    }, [])

    const handleAdd = () => {
        setEditingProduct(null)
        setIsModalVisible(true)
    }

    const handleEdit = (record) => {
        setEditingProduct({
            ...record,
            specifications: record.specifications || []
        })
        setIsModalVisible(true)
    }

    const handleDelete = (key) => {
        setProducts(products.filter((product) => product.key !== key))
    }

    const handleOk = (values) => {
        values.specifications = values.specifications.map((spec) => ({
            key: spec.key,
            value: parseSpecificationValue(spec.value)
        }))

        if (editingProduct) {
            setProducts(products.map((product) => (product.key === editingProduct.key ? { ...values, key: editingProduct.key } : product)))
        } else {
            setProducts([...products, { ...values, key: Date.now() }])
        }
        setIsModalVisible(false)
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }

    const handleChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter)
    }

    const columns = [
        {
            title: "Produkt",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            sortOrder: sortedInfo.columnKey === "name" && sortedInfo.order
        },
        {
            title: "Modell",
            dataIndex: "model",
            key: "model",
            sorter: (a, b) => a.model.localeCompare(b.model),
            sortOrder: sortedInfo.columnKey === "model" && sortedInfo.order
        },
        {
            title: "Problemlösning",
            dataIndex: "description",
            key: "description",
            render: (description) => (
                <>
                    {description.map((item, index) => (
                        <Tag color={colorMap[item]} key={index}>
                            {item}
                        </Tag>
                    ))}
                </>
            )
        },
        {
            title: "Artikelnummer",
            dataIndex: "articleNumbers",
            key: "articleNumbers",
            sorter: (a, b) => a.articleNumbers - b.articleNumbers,
            sortOrder: sortedInfo.columnKey === "articleNumbers" && sortedInfo.order
        },
        {
            title: "Pris",
            dataIndex: "price",
            key: "price",
            sorter: (a, b) => a.price - b.price,
            sortOrder: sortedInfo.columnKey === "price" && sortedInfo.order
        },
        {
            title: "Åtgärd",
            key: "action",
            render: (text, record) => (
                <>
                    <Button onClick={() => handleEdit(record)}>Redigera</Button>
                    <Popconfirm
                        title="Är du säker på att ta bort den här produkten?"
                        onConfirm={() => handleDelete(record.key)}
                        cancelText="Avbryt"
                    >
                        <Button type="danger" style={{ marginLeft: 8 }}>
                            Radera
                        </Button>
                    </Popconfirm>
                </>
            )
        }
    ]

    const filteredProducts = products.filter((product) => {
        return (
            product.name.toLowerCase().includes(searchText.toLowerCase()) ||
            product.model.toLowerCase().includes(searchText.toLowerCase()) ||
            product.description.some((desc) => desc.toLowerCase().includes(searchText.toLowerCase())) ||
            product.articleNumbers.toLowerCase().includes(searchText.toLowerCase())
        )
    })

    const tableLocale = {
        triggerDesc: "Klicka för att sortera fallande",
        triggerAsc: "Klicka för att sortera stigande",
        cancelSort: "Klicka för att avbryta sortering"
    }

    return (
        <>
            <Input
                placeholder="Sök"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ marginBottom: 16, width: "50%" }}
                prefix={<SearchOutlined />}
            />
            <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16, position: "absolute", right: 15 }}>
                Lägg till produkt
            </Button>
            <Table columns={columns} dataSource={filteredProducts} onChange={handleChange} pagination={false} locale={tableLocale} />
            <Modal
                title={editingProduct ? "Redigera" : "Lägg till"}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose
            >
                <Form
                    key={editingProduct ? editingProduct.key : "new"}
                    initialValues={{
                        ...editingProduct,
                        specifications: editingProduct ? editingProduct.specifications.map((spec) => ({ ...spec, value: spec.value })) : []
                    }}
                    onFinish={handleOk}
                    layout="vertical"
                >
                    <Form.Item name="name" label="Produkt" rules={[{ required: true, message: "Vänligen ange produktnamn" }]}>
                        <Input disabled={!!editingProduct} />
                    </Form.Item>
                    <Form.Item name="model" label="Modell" rules={[{ required: true, message: "Vänligen ange modellnamn" }]}>
                        <Input disabled={!!editingProduct} />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Problemlösning"
                        rules={[{ required: true, message: "Vänligen ange problemlösning" }]}
                    >
                        <Select mode="multiple" placeholder="Välj problemlösningar">
                            {problemOptions.map((option) => (
                                <Option key={option} value={option}>
                                    {option}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="articleNumbers"
                        label="Artikelnummer"
                        rules={[{ required: true, message: "Vänligen ange artikelnummer" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="price" label="Pris" rules={[{ required: true, message: "Vänligen ange pris" }]}>
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item name="specifications" label="Specifikationer">
                        <Form.List name="specifications">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                                        <div key={key} style={{ display: "flex", marginBottom: 8 }}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "key"]}
                                                fieldKey={[fieldKey, "key"]}
                                                rules={[{ required: true, message: "Nyckel är obligatorisk" }]}
                                                style={{ flex: 3, marginRight: 8 }}
                                            >
                                                <Select placeholder="Välj specifikation" disabled={!!editingProduct}>
                                                    {specificationsOptions.map((option) => (
                                                        <Option key={option} value={option}>
                                                            {option}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "value"]}
                                                fieldKey={[fieldKey, "value"]}
                                                rules={[{ required: true, message: "Värde är obligatoriskt" }]}
                                                style={{ flex: 2 }}
                                            >
                                                <Input placeholder="Värde" />
                                            </Form.Item>
                                            {!editingProduct && (
                                                <Button type="danger" onClick={() => remove(name)}>
                                                    -
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    {!editingProduct && (
                                        <Form.Item>
                                            <Button type="dashed" onClick={() => add()} block>
                                                Lägg till specifikation
                                            </Button>
                                        </Form.Item>
                                    )}
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {editingProduct ? "Uppdatera" : "Lägg till"}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}

export default ProductManagement
